import { type ReactNode } from "react";
import { styled } from "@mui/material/styles";
import { Button, Card, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import { C_ON_PRIMARY_CONTAINER, C_OUTLINE_VARIANT, C_PRIMARY_CONTAINER } from "../icon/Colors";
import { stacker } from "~/styles/styledMixins";
import ArrowRightOutlinedIcon from "@mui/icons-material/ArrowRightOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { useTranslation } from "react-i18next";
import { C_ON_SURFACE_VARIANT } from "~/common/components/icon/Colors";

export interface LinkListProps {
  title: string;
  titleIcon?: ReactNode;
  seeMoreUrl?: string;
  children: ReactNode;
}

export const LinkList = ({ children, seeMoreUrl, title, titleIcon }: LinkListProps) => {
  const { t } = useTranslation();
  return (
    <LinkListRoot aria-label={title}>
      <Card>
        <Header component={"div"}>
          {titleIcon}
          <Typography variant="titleLarge">{title}</Typography>
        </Header>
        <List disablePadding>{children}</List>
      </Card>
      {seeMoreUrl && (
        <Button href={seeMoreUrl} variant="text">
          {t("page.sa.housing.seemore")}
        </Button>
      )}
    </LinkListRoot>
  );
};

const LinkListRoot = styled("nav")({
  ...stacker("16px"),
  flexDirection: "column",
});

const Header = styled(ListItem)({
  background: C_PRIMARY_CONTAINER,
  color: C_ON_PRIMARY_CONTAINER,
  fontWeight: "bold",
  ...stacker("16px"),
  alignItems: "center",
  borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
}) as unknown as typeof ListItem;

type LinkType = "NEWTAB" | "SAMETAB" | "DOWNLOAD";

export interface LinkListItemProps {
  overline?: string;
  headline?: string;
  subheadline?: string;
  href: string;
  linkType: LinkType;
}

const ICONS: Record<LinkType, ReactNode> = {
  NEWTAB: <LaunchOutlinedIcon />,
  SAMETAB: <ArrowRightOutlinedIcon />,
  DOWNLOAD: <FileDownloadOutlinedIcon />,
};

export const LinkListItem = ({ href, overline, headline, subheadline, linkType }: LinkListItemProps) => (
  <StyledListItem disablePadding>
    <StyledListItemButton component={"a"} href={href} target={linkType === "NEWTAB" ? "_blank" : undefined}>
      <ListItemText disableTypography>
        <Typography variant="labelMedium" sx={{ color: C_ON_SURFACE_VARIANT }}>
          {overline}
        </Typography>
        <Typography variant="bodyLarge">{headline}</Typography>
        <Typography>{subheadline}</Typography>
      </ListItemText>
      <IconContainer>{ICONS[linkType]}</IconContainer>
    </StyledListItemButton>
  </StyledListItem>
);

const StyledListItem = styled(ListItem)({
  "&:last-child a": {
    border: "none",
  },
}) as unknown as typeof ListItem;

const StyledListItemButton = styled(ListItemButton)({
  borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
  gap: "16px",
}) as unknown as typeof ListItemButton;

const IconContainer = styled("div")({
  width: "24px",
  flex: "none",
  display: "flex",
  alignSelf: "stretch",

  containerType: "size",
  alignItems: "center",
  ["@container (min-height: 48px)"]: {
    "> *": {
      alignSelf: "flex-start",
    },
  },
});
